import { Box, Flex, Text } from '@chakra-ui/react';
import React, { useMemo, useState } from 'react';
import { InfoAlert } from 'src/components/atoms/alerts/InfoAlert';
import {
  DataBox,
  DataBoxBaseProps,
  DataBoxLegendProps,
} from 'src/components/molecules/DataBox';
import { NewAreaGraph } from 'src/components/molecules/graphs/NewAreaGraph';
import { NewBarGraph } from 'src/components/molecules/graphs/NewBarGraph';
import { useFilterContext } from 'src/contexts/FilterContext';
import { useSelectedDimensionContext } from 'src/features/MM/contexts/SelectedDimensionContext';
import { useGetDateAxisFormatter } from 'src/features/MM/hooks/useGetDateAxisFormatter';
import { Dimension, MMDataState } from 'src/features/MM/types';
import { benchmarkColor } from 'src/theme';
import { PercentageValueTickFormatter } from 'src/utils/TickFormatters';
import { ChartType, ChartTypeTabs } from '../../molecules/ChartTypeTabs';
import {
  PecentOrUnit,
  PercentOrUnitTabs,
} from '../../molecules/PercentOrUnitTabs';
import { TrendsDataTableModal } from '../../molecules/TrendsDataTableModal';
import { OTHER_LABEL, useChartData } from './useChartData';

const OTHER_COLOR = '#A0AEC0';

type TrendsChartBoxProps = {
  metricState: MMDataState;
  totalLineLabel?: string;
  unitTabLabel?: string;
  yAxisFormatter: (value: number | string) => string;
  infoTitle?: string;
  infoBodyText: string;
} & DataBoxBaseProps;

export const TrendsChartBox: React.FC<TrendsChartBoxProps> = ({
  metricState,
  yAxisFormatter,
  totalLineLabel,
  unitTabLabel,
  infoBodyText,
  infoTitle,
  ...props
}) => {
  const { mediaColorMap } = useFilterContext();

  const { dimension } = useSelectedDimensionContext();
  const [chartType, setChartType] = useState<ChartType>('bar');
  const [percentOrUnit, setPercentOrUnit] = useState<PecentOrUnit>('unit');

  const [dataModalIsOpen, setDataModalIsOpen] = useState(false);

  const xAxisFormatter = useGetDateAxisFormatter();

  const shouldUsePercentData = percentOrUnit === 'percent';

  const { legendKeys, data } = useChartData(
    metricState,
    shouldUsePercentData,
    totalLineLabel,
  );

  const [infoOpen, setInfoOpen] = useState(false);
  const legendProps = useMemo(() => {
    const keyColorMap: Record<string, string> =
      dimension === Dimension.media ? { ...mediaColorMap } : {};

    if (legendKeys.includes(OTHER_LABEL)) {
      keyColorMap[OTHER_LABEL] = OTHER_COLOR;
    }

    const lProps: DataBoxLegendProps = {
      baseLegendKeys: legendKeys,
      placement: 'right',
      lineKeysFirst: true,
      keyColorMap,
    };

    if (totalLineLabel) {
      lProps.lineKeys = [totalLineLabel];
      keyColorMap[totalLineLabel] = benchmarkColor;
    }

    return lProps;
  }, [dimension, legendKeys, mediaColorMap, totalLineLabel]);

  const yFormatter = shouldUsePercentData
    ? PercentageValueTickFormatter
    : yAxisFormatter;

  return (
    <>
      <InfoAlert
        headerText={infoTitle ?? props.title ?? ''}
        bodyText={infoBodyText}
        isOpen={infoOpen}
        onClose={() => setInfoOpen(false)}
      />

      <DataBox
        {...props}
        onInfoButtonClick={() => setInfoOpen(true)}
        restrictChartHeight
        exportEnabled
        exportButtonEnabled={false}
        equalWidthLegend
        tabs={<ChartTypeTabs onSelect={setChartType} selectedTab={chartType} />}
        secondaryTabs={
          unitTabLabel ? (
            <PercentOrUnitTabs
              unitLabel={unitTabLabel}
              onSelect={setPercentOrUnit}
              selectedTab={percentOrUnit}
            />
          ) : undefined
        }
        legendProps={legendProps}
        setTableDataModalIsOpen={setDataModalIsOpen}
        hasData={data.length > 0}
        infoNextToTitle
        isError={metricState.isError}
        isLoading={metricState.isLoading}>
        <Flex direction="column" flex="1" minH={0} w="100%">
          <TrendsDataTableModal
            setIsOpen={setDataModalIsOpen}
            isOpen={dataModalIsOpen}
            data={data}
            yFormatter={yFormatter}
          />
          <Box flex="1" minH={0}>
            {chartType === 'line' && (
              <NewAreaGraph
                percentRange={shouldUsePercentData}
                data={data}
                xAxisKey={'utcEpoch'}
                xAxisFormatter={xAxisFormatter}
                yAxisFormatter={yFormatter}
                stacked={true}
              />
            )}
            {chartType === 'bar' && (
              <NewBarGraph
                percentRange={shouldUsePercentData}
                data={data}
                xAxisKey={'utcEpoch'}
                stacked
                xAxisFormatter={xAxisFormatter}
                yAxisFormatter={yFormatter}
              />
            )}
          </Box>
          {legendKeys.includes(OTHER_LABEL) && (
            <Text
              fontFamily="marketingPage"
              fontSize="xs"
              color="text.darkGray"
              textAlign="center"
              mt={2}>
              Showing top 15 campaigns by{' '}
              {(props.title ?? '').toString().toLowerCase()}. Adjust the
              campaign filter to focus on specific ones.
            </Text>
          )}
        </Flex>
      </DataBox>
    </>
  );
};
